/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { Box, Text, theme } from "@nordcloud/gnui";
import {
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ErrorWrap,
  LoaderWrap,
  Pagination,
  ReactTable,
} from "~/components";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateSort,
  useFilteredQueryState,
} from "~/hooks";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  EstateRecordsLoader,
  ChartParametersBar,
  FiltersProp,
  useEstateParameters,
  useEstateRecords,
  ParametersBar,
  EstateTotals,
} from "./components";
import { EstateListColumns } from "./EstateList";
import { convertTags, generateNewRowData, getMultiResources } from "./helpers";

const BREADCRUMBS = [
  {
    value: "estate-records",
    label: "Estate Records",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

type Props = {
  envNid?: string;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type QueryState = EstateV2Filter &
  FiltersProp &
  SortingProp & {
    page: number;
    limit: number;
    query: string;
  };

export function EstateRecordsPage({ envNid }: Props) {
  const {
    period,
    range,
    granularity,
    setGranularity,
    handlePeriodChange,
    search,
    setSearch,
  } = useEstateParameters();
  const { currency } = useCurrency();
  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page, limit, query, field, order, ...restFilters }: QueryState =
    state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const estateFilters = {
    filter: {
      ...restFilters,
      metadata: convertTags(restFilters.metadata),
      tags: convertTags(restFilters.tags),
    },
    query,
    sort: {
      field: sortField,
      order: sortDirection,
    },
  };

  const { data, loading, error, refetch } = useEstateRecords(
    {
      variables: {
        page,
        limit,
        ...estateFilters,
      },
    },
    (message) => showError(message)
  );

  const [selectedResources, setSelectedResources] = useState<string[]>([]);

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency, envNid),
      })) ?? [],
    [data, currency, envNid]
  );

  const hasResources = isNotEmpty(rows);
  const estateCount = data?.count ?? 0;
  const showPagination = estateCount > 0;

  const columns = useMemo(() => EstateListColumns, []);

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const selectedRowsHandler = useCallback(
    (items: string[]) => {
      setSelectedResources(
        items.filter((item) =>
          data?.resources
            ?.filter((row) => row.__typename !== "CostResource")
            .map((row) => row.id)
            .includes(item)
        )
      );
    },
    [setSelectedResources, data?.resources]
  );

  return (
    <div>
      <BreadcrumbsBox
        title="Estate Records"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <ChartParametersBar
          period={period}
          range={range}
          granularity={granularity}
          restFilters={restFilters}
          handleFilters={(newFilters) =>
            refetch({ variables: { filter: newFilters } })
          }
          onPeriodChange={handlePeriodChange}
          onGranularityChange={setGranularity}
        />
        <EstateTotals
          period={period}
          range={range}
          costLoading={loading}
          totalCost={data?.cost?.currentMonthTotal}
          count={data?.count}
          monthlySavings={data?.savingsAmount?.total}
        />
        <ParametersBar
          disabledAttachment={isEmpty(selectedResources)}
          search={search}
          setSearch={setSearch}
          sortDirection={sortDirection}
          sortField={sortField}
          resources={resources}
          refresh={refetch}
          filters={estateFilters}
          recordsCount={data?.count ?? 0}
          onSortOrderChange={onSortOrderChange}
          onSortSelect={onSortSelect}
        />
        <ErrorWrap
          error={error}
          status={
            hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger
          }
        >
          <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
            <If condition={hasResources}>
              <Then>
                <ReactTable
                  columns={columns}
                  data={rows}
                  sort={false}
                  headerBackground={theme.color.background.ui03}
                  deselectRows={isEmpty(selectedResources ?? [])}
                  onSelectedRowsChange={selectedRowsHandler}
                />
                <When condition={showPagination}>
                  <Pagination count={estateCount} />
                </When>
              </Then>
              <Else>
                <Text
                  paddingTop={theme.spacing.spacing04}
                  paddingBottom={theme.spacing.spacing04}
                >
                  No resources matching selected criteria
                </Text>
              </Else>
            </If>
          </LoaderWrap>
        </ErrorWrap>
      </Box>
    </div>
  );
}
